import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (!token) setError('Invalid or missing reset token. Please request a new link.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmPassword) return setError('Please fill in both fields.');
    if (newPassword.length < 6) return setError('Password must be at least 6 characters.');
    if (newPassword !== confirmPassword) return setError('Passwords do not match.');

    setLoading(true);
    try {
      const res = await authAPI.resetPassword(token, newPassword);
      const role = res.data?.role || 'student';
      setUserRole(role);
      setSuccess(true);
      // ONLY redirect to /login if the user is an Administrator!
      // Students, parents, and tutors must NEVER be sent to the Admin Login page.
      if (role === 'admin') {
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired. Please request a new one.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Visual Panel */}
      <div className="login-visual" style={{
        background: 'linear-gradient(rgba(123, 29, 58, 0.75), rgba(17, 17, 17, 0.85)), url("/assets/login-bg.jpeg") center/cover no-repeat',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '60px', color: '#fff'
      }}>
        <div style={{ marginBottom: 40 }}>
          <img src="/assets/logo.png" alt="ETS" style={{ width: 60, height: 60, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', marginBottom: 20 }} />
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 700, lineHeight: 1.1 }}>
            Set New<br />Password
          </h1>
        </div>
        <p style={{ fontSize: 18, opacity: 0.7, maxWidth: 400, lineHeight: 1.6 }}>
          Choose a strong password with at least 6 characters. You'll use this to log in going forward.
        </p>
      </div>

      {/* Form Panel */}
      <div className="login-form-side" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 400, width: '100%', padding: '40px' }}>

          {success ? (
            /* Success State */
            userRole === 'admin' ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 56, marginBottom: 20 }}>✅</div>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: '#111', marginBottom: 12 }}>
                  Admin Password Reset!
                </h2>
                <p style={{ color: '#555', fontSize: 15, lineHeight: 1.6, marginBottom: 8 }}>
                  Your administrator password has been updated successfully.
                </p>
                <p style={{ color: '#999', fontSize: 13, marginBottom: 32 }}>
                  Redirecting to Admin Login in a moment...
                </p>
                <Link to="/login" style={{
                  display: 'inline-block', background: 'linear-gradient(135deg,#7b1d3a,#c04060)',
                  color: '#fff', padding: '12px 32px', borderRadius: 8, textDecoration: 'none',
                  fontSize: 14, fontWeight: 600
                }}>
                  Go to Admin Login →
                </Link>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 56, marginBottom: 16 }}>📱</div>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: '#111', marginBottom: 10 }}>
                  Password Reset Complete!
                </h2>
                <div style={{
                  background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 10,
                  padding: '14px 18px', marginBottom: 20, textAlign: 'center'
                }}>
                  <p style={{ color: '#2e7d32', fontSize: 14, fontWeight: 600, margin: 0 }}>
                    ✅ Your new password is now active!
                  </p>
                </div>
                <p style={{ color: '#444', fontSize: 15, lineHeight: 1.6, marginBottom: 12 }}>
                  Please return to the <strong>ETS Mobile App</strong> on your phone and log in with your new password.
                </p>
                <p style={{ color: '#888', fontSize: 13, lineHeight: 1.5, marginBottom: 28 }}>
                  Students, parents, and tutors use the ETS Mobile App to view schedules, attendance, and learning materials.
                </p>
                
                <div style={{
                  background: '#f9f9f9', border: '1px dashed #ddd', borderRadius: 10,
                  padding: '16px', color: '#666', fontSize: 13, lineHeight: 1.5
                }}>
                  ✨ <strong>All set!</strong> You can now close this browser window and open the ETS app.
                </div>
              </div>
            )
          ) : (
            /* Form State */
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 40 }}>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: '#111', marginBottom: 8 }}>
                  Reset Password
                </h2>
                <p style={{ color: '#9a9a9a', fontSize: 14 }}>
                  Enter and confirm your new password below.
                </p>
              </div>

              {error && (
                <div style={{
                  background: 'rgba(192, 57, 43, 0.05)', color: '#c0392b',
                  padding: '12px 16px', fontSize: 13, borderLeft: '3px solid #c0392b', marginBottom: 24
                }}>
                  {error}
                  {error.includes('expired') && (
                    <div style={{ marginTop: 8 }}>
                      <Link to="/forgot-password" style={{ color: '#c0392b', fontWeight: 600 }}>
                        Request a new link →
                      </Link>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label-style">New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input-style"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimum 6 characters"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    required
                    autoFocus
                    style={{ paddingRight: 48 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#999'
                    }}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 32 }}>
                <label className="form-label-style">Confirm New Password</label>
                <input
                  className="form-input-style"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>Passwords do not match</p>
                )}
                {confirmPassword && newPassword === confirmPassword && (
                  <p style={{ color: '#27ae60', fontSize: 12, marginTop: 6 }}>✅ Passwords match</p>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', padding: '14px', justifyContent: 'center' }}
                disabled={loading || !token}
              >
                {loading ? 'Saving Password...' : 'Set New Password →'}
              </button>

              <div style={{ marginTop: 32, textAlign: 'center', paddingTop: 24, borderTop: '1px solid #eee' }}>
                <Link to="/login" style={{ color: '#9a9a9a', fontSize: 13, textDecoration: 'none' }}>
                  {t('common.backToLogin', '← Back to Login')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
